import { useUrlGenerator } from '@folklore/routes';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { defineMessage, FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';

import StyledButton from '../../buttons/StyledButton';
import PlayDefault from '../../pages/PlayDefault';

import styles from '../../../../styles/themes/rhoneacademy/play-rhoneacademy.module.scss';

import lockedSvg from '../../../../img/rhoneacademy/games/locked.svg';
import unlockedSvg from '../../../../img/rhoneacademy/games/unlocked.svg';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    onTimerComplete: PropTypes.func,
    canPlay: PropTypes.bool,
};

const defaultProps = {
    onTimerComplete: null,
    canPlay: false,
};

function PlayRhoneAcademy({ page, onTimerComplete, canPlay }) {
    const { games = null, participations = null } = page;

    const route = useUrlGenerator();
    const { formatMessage } = useIntl();

    const learningKitLabel = defineMessage({
        defaultMessage: 'Learning kit',
        description: 'Rhone Academy - Learning kit CTA',
    });
    const learningGuideLink = defineMessage({
        defaultMessage:
            'https://www.vins-rhone.com/sites/vignoble/files/documentation/2024-12/Kit-pedagogique-2024-EN.pdf',
        description: 'Rhone Academy - Learning kit link',
    });

    const youtubeLabel = defineMessage({
        defaultMessage: 'YouTube videos',
        description: 'Rhone Academy - YouTube CTA',
    });

    const websiteLabel = defineMessage({
        defaultMessage: 'Website',
        description: 'Rhone Academy - Website CTA',
    });
    const websiteLink = defineMessage({
        defaultMessage: 'https://www.vins-rhone.com/en',
        description: 'Rhone Academy - Website CTA Link',
    });

    const webinarsLabel = defineMessage({
        defaultMessage: 'Webinars',
        description: 'Rhone Academy - Webinars CTA',
    });

    const finalGames = useMemo(() => {
        // add isCompleted to questions groups
        const newArray = games.map((game) => {
            const { id: gameId = null, questionsGroups = null } = game;
            const newQuestionsGroups = (questionsGroups || []).map((group) => {
                const participationForGame =
                    participations.find((p) => p.gameId === gameId) || null;
                if (participationForGame === null) {
                    return group;
                }

                const { answers: answersForGame = null } = participationForGame;
                const { questions: groupQuestions = null } = group;
                const answersForGroup = (answersForGame || []).filter(({ question }) =>
                    groupQuestions.find(({ id: questionId }) => questionId === question),
                );

                const groupQuestionsCount = (groupQuestions || []).length;
                const validatedAnswersCount = answersForGroup.filter(
                    (a) => a.validated === true,
                ).length;

                return {
                    ...group,
                    isCompleted:
                        groupQuestionsCount > 0 && groupQuestionsCount === validatedAnswersCount,
                };
            });

            return {
                ...game,
                questionsGroups: newQuestionsGroups,
            };
        });

        // sort games by title in alphabetical order
        newArray.sort((a, b) => {
            if (a.title > b.title) {
                return 1;
            }
            if (a.title < b.title) {
                return -1;
            }
            return 0;
        });
        return newArray;
    }, [games]);

    const [firstGameStart = null] = useMemo(
        () => games.map(({ startAt }) => parseISO(startAt)).sort((a, b) => isAfter(a, b)),
        [finalGames],
    );
    const now = new Date();
    const firstGameStarted = isAfter(now, firstGameStart);

    if (!firstGameStarted) {
        return <PlayDefault page={page} onTimerComplete={onTimerComplete} canPlay={canPlay} />;
    }

    const formattedLearningKitLabel = formatMessage(learningKitLabel);
    const formattedYoutubeLabel = formatMessage(youtubeLabel);
    const formattedWebsiteLabel = formatMessage(websiteLabel);
    const formattedWebinarsLabel = formatMessage(webinarsLabel);

    return (
        <div className={classNames(styles.container, 'noHeader')}>
            <h1>Modules</h1>

            <div className={styles.learningGuideBlock}>
                <h2>
                    <FormattedMessage
                        defaultMessage="Learning guide"
                        description="Rhone Academy - Play - Links header"
                    />
                </h2>

                <a
                    className={styles.cta}
                    href={formatMessage(learningGuideLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={formattedLearningKitLabel}
                >
                    <div>{formattedLearningKitLabel}</div>
                    <div className={styles.icon}>
                        <FontAwesomeIcon icon={faExternalLink} size="xs" />
                    </div>
                </a>

                <a
                    className={styles.cta}
                    href="https://www.youtube.com/@VinsRhone/playlists"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={formattedYoutubeLabel}
                >
                    <div>{formattedYoutubeLabel}</div>
                    <div className={styles.icon}>
                        <FontAwesomeIcon icon={faExternalLink} size="xs" />
                    </div>
                </a>

                <a
                    className={styles.cta}
                    href={formatMessage(websiteLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={formattedWebsiteLabel}
                >
                    <div>{formattedWebsiteLabel}</div>
                    <div className={styles.icon}>
                        <FontAwesomeIcon icon={faExternalLink} size="xs" />
                    </div>
                </a>

                <a
                    className={styles.cta}
                    href="https://www.youtube.com/playlist?list=PLm9_GI1qYavA3cbusm7RaWheeUuQYHXVn"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={formattedWebinarsLabel}
                >
                    <div>{formattedWebinarsLabel}</div>
                    <div className={styles.icon}>
                        <FontAwesomeIcon icon={faExternalLink} size="xs" />
                    </div>
                </a>
            </div>

            <div className={styles.modules}>
                <div className={styles.line} />
                {finalGames.map(
                    ({ id: gameId, title, image, startAt, endAt, questionsGroups = [] }) => {
                        const isStarted = isAfter(now, parseISO(startAt));
                        const isFinished = endAt !== null ? isAfter(now, parseISO(endAt)) : false;
                        const isUnlocked = isStarted && !isFinished;
                        return (
                            <div
                                key={gameId}
                                className={classNames(styles.section, {
                                    [styles.unlocked]: isUnlocked,
                                })}
                            >
                                <div className={styles.icon}>
                                    {isUnlocked ? (
                                        <img src={unlockedSvg} alt="Unlocked" />
                                    ) : (
                                        <img src={lockedSvg} alt="Locked" />
                                    )}
                                </div>
                                <div className={styles.module}>
                                    <h2>{title}</h2>
                                    <img src={image.url} alt={title} />
                                    {questionsGroups.map(
                                        ({
                                            id: groupId,
                                            label: groupLabel,
                                            canAnswer: canAnswerGroup,
                                            startAt: groupStartAt = null,
                                            isCompleted = null,
                                        }) => {
                                            const enabled = isUnlocked && canAnswerGroup;
                                            const gameBaseUrl = route('game', {
                                                game: gameId,
                                            });
                                            const href = `${gameBaseUrl}/${groupId}`;

                                            return (
                                                <StyledButton
                                                    key={groupId}
                                                    className={classNames(styles.playButton, {
                                                        [styles.unlocked]: enabled,
                                                        [styles.isCompleted]: isCompleted,
                                                    })}
                                                    href={href}
                                                    disabled={!enabled}
                                                >
                                                    {isUnlocked && canAnswerGroup ? (
                                                        <span>{groupLabel}</span>
                                                    ) : (
                                                        <div className={styles.lockedGroupMsg}>
                                                            {isFinished ? (
                                                                <FormattedMessage
                                                                    defaultMessage="Module finished"
                                                                    description="Rhone Academy - Module label"
                                                                />
                                                            ) : (
                                                                <FormattedMessage
                                                                    defaultMessage="Starts on {date}"
                                                                    description="Rhone Academy - Play - Starting On"
                                                                    values={{
                                                                        date: (
                                                                            <FormattedDate
                                                                                value={
                                                                                    groupStartAt ||
                                                                                    startAt
                                                                                }
                                                                            />
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </StyledButton>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        );
                    },
                )}
            </div>
        </div>
    );
}
PlayRhoneAcademy.propTypes = propTypes;
PlayRhoneAcademy.defaultProps = defaultProps;

export default PlayRhoneAcademy;
